/**
 * ContextHelpDrawer — v2.39
 * Right-side sliding help panel covering all 28 manual sections.
 *
 * Features:
 *  - Boolean search (AND / OR / NOT / -term) across every section
 *  - Panel navigation chips for direct jump
 *  - Every entry: purpose, prerequisites, step-by-step, next step
 *  - Linked keywords [label](panel_key) rendered as chips
 *
 * Triggered from:
 *  1. ActivityBar "?" button (onOpenHelp prop)
 *  2. Help menu › "Context Help" item
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  BookOpen, PenLine, BookText, Users, LineChart,
  Activity, MessagesSquare, TrendingUp, Layers,
  SlidersHorizontal, Library, HelpCircle, ChevronDown, ChevronRight,
  Lightbulb, ExternalLink, ArrowRight, Target, Headphones, WifiOff,
  Lock, Zap, Search, X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PANEL_TIPS, ALWAYS_NEXT_TIP } from '@/lib/helpDrawerDefaults';

/* ─── Panel metadata ──────────────────────────────────────────────────── */

const PANEL_META = {
  home:       { icon: BookOpen,          label: 'Getting Started',     colour: 'text-emerald-600' },
  editor:     { icon: PenLine,           label: 'Manuscript Editor',   colour: 'text-rose-700'   },
  chapters:   { icon: BookText,          label: 'Chapters Panel',      colour: 'text-sky-600'    },
  bible:      { icon: Users,             label: 'Character Bible',     colour: 'text-violet-600' },
  arc:        { icon: LineChart,         label: 'Arc Visualizer',      colour: 'text-amber-600'  },
  analysis:   { icon: Activity,          label: 'Analysis',            colour: 'text-teal-600'   },
  chat:       { icon: MessagesSquare,    label: 'AI Chat',             colour: 'text-indigo-600' },
  marketing:  { icon: TrendingUp,        label: 'Marketing & Business',colour: 'text-orange-600' },
  modes:      { icon: Layers,            label: 'Editing Modes',       colour: 'text-pink-600'   },
  variables:  { icon: SlidersHorizontal, label: 'Project Variables',   colour: 'text-stone-600'  },
  bookshelf:  { icon: Library,           label: 'My Bookshelf',        colour: 'text-lime-600'   },
  word_count: { icon: Target,            label: 'Word Count',          colour: 'text-cyan-600'   },
  narration:  { icon: Headphones,        label: 'Narration Studio',    colour: 'text-purple-600' },
  offline:    { icon: WifiOff,           label: 'Offline Audio',       colour: 'text-slate-600'  },
  tiers:      { icon: Lock,              label: 'Subscriptions',       colour: 'text-blue-600'   },
  coach:      { icon: Zap,              label: 'Live Coach',           colour: 'text-yellow-600' },
};

/* ─── Boolean search parser ───────────────────────────────────────────── */

function parseBooleanQuery(raw) {
  const q = raw.trim();
  if (!q) return null;

  // OR — any clause must match
  if (/ OR /i.test(q)) {
    return {
      type: 'OR',
      clauses: q.split(/ OR /i).map((c) => c.trim().toLowerCase()),
    };
  }

  // AND or space-delimited (all terms must match, -term / NOT term to exclude)
  const separator = / AND /i.test(q) ? / AND /i : /\s+/;
  const parts = q.split(separator).map((p) => p.trim()).filter(Boolean);
  const must = [];
  const mustNot = [];
  parts.forEach((p) => {
    if (p.startsWith('-') && p.length > 1) {
      mustNot.push(p.slice(1).toLowerCase());
    } else if (/^NOT$/i.test(p)) {
      // standalone NOT keyword — skip
    } else if (/^NOT /i.test(p)) {
      mustNot.push(p.replace(/^NOT /i, '').toLowerCase());
    } else {
      must.push(p.toLowerCase());
    }
  });
  return { type: 'AND', must, mustNot };
}

function textMatchesParsed(text, parsed) {
  if (!parsed) return true;
  const lower = text.toLowerCase();
  if (parsed.type === 'OR') return parsed.clauses.some((c) => lower.includes(c));
  return (
    parsed.must.every((t) => lower.includes(t)) &&
    !parsed.mustNot.some((t) => lower.includes(t))
  );
}

/** Build flat list of search-candidate sections (with panel metadata). */
function buildSearchIndex() {
  return Object.entries(PANEL_TIPS).flatMap(([panelKey, cfg]) =>
    (cfg.sections || []).map((section) => ({
      panelKey,
      panelLabel: PANEL_META[panelKey]?.label || panelKey,
      panelColour: PANEL_META[panelKey]?.colour || 'text-muted-foreground',
      panelIcon: PANEL_META[panelKey]?.icon || HelpCircle,
      title: section.title,
      tips: section.tips,
    }))
  );
}

const SEARCH_INDEX = buildSearchIndex();

function runSearch(parsed) {
  if (!parsed) return [];
  return SEARCH_INDEX
    .map((entry) => {
      const titleMatch = textMatchesParsed(entry.title, parsed);
      const matchingTips = entry.tips.filter((t) => textMatchesParsed(t, parsed));
      if (!titleMatch && matchingTips.length === 0) return null;

      // When section title matches, use all tips — but still honour mustNot
      // so tips containing excluded terms don't bleed through.
      let tipsToShow = titleMatch ? entry.tips : matchingTips;
      const mustNot = parsed.type === 'AND' ? (parsed.mustNot || []) : [];
      if (mustNot.length > 0) {
        tipsToShow = tipsToShow.filter(
          (tip) => !mustNot.some((t) => tip.toLowerCase().includes(t))
        );
      }

      if (!tipsToShow.length) return null;
      return { ...entry, tips: tipsToShow };
    })
    .filter(Boolean);
}

/* ─── Highlight helper ────────────────────────────────────────────────── */

/** Extract the terms to bold from a parsed boolean query. */
function getHighlightTerms(parsed) {
  if (!parsed) return [];
  if (parsed.type === 'OR') return parsed.clauses;
  return parsed.must || [];
}

/**
 * Wrap each match of `terms` in a <mark> inside a plain-text segment.
 * Uses capturing-group split so odd-indexed parts are the matches.
 */
function HighlightedText({ text, terms }) {
  if (!terms?.length) return <span>{text}</span>;
  const escaped = terms
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .filter(Boolean);
  if (!escaped.length) return <span>{text}</span>;
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark
            key={i}
            className="bg-amber-200/80 dark:bg-amber-700/40 rounded-sm px-0.5 font-semibold not-italic"
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

/* ─── Link renderer ───────────────────────────────────────────────────── */

function renderTipText(text, onNavigate, highlightTerms) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (m) {
      return (
        <button
          key={i}
          type="button"
          onClick={() => onNavigate(m[2])}
          className="inline-flex items-center gap-0.5 rounded-full border border-amber-400/60 bg-amber-50 px-1.5 py-0 text-[10.5px] font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-600/40 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-900/40 transition-colors"
          title={`Jump to ${m[1]} help`}
        >
          {m[1]}
        </button>
      );
    }
    return highlightTerms?.length
      ? <HighlightedText key={i} text={part} terms={highlightTerms} />
      : <span key={i}>{part}</span>;
  });
}

/* ─── TipSection ──────────────────────────────────────────────────────── */

function TipSection({ section, onNavigate, panelBadge, highlightTerms }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left bg-muted/40 hover:bg-muted/70 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {panelBadge && (
            <span className={cn('shrink-0 text-[9px] font-bold uppercase tracking-widest', panelBadge.colour)}>
              {panelBadge.label}
            </span>
          )}
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground truncate">
            {section.title}
          </span>
        </div>
        {open
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>
      {open && (
        <ul className="divide-y divide-border">
          {section.tips.map((tip, i) => (
            <li key={i} className="flex gap-2.5 px-3 py-2.5">
              <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
              <span className="text-xs leading-relaxed text-foreground">
                {renderTipText(tip, onNavigate, highlightTerms)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─── Next Step banner ────────────────────────────────────────────────── */

function NextStepBanner({ panel, onNavigate }) {
  const next = ALWAYS_NEXT_TIP[panel];
  if (!next) return null;
  const nextMeta = PANEL_META[next.panel];
  if (!nextMeta) return null;
  const NextIcon = nextMeta.icon;
  return (
    <button
      type="button"
      onClick={() => onNavigate(next.panel)}
      className="flex w-full items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-left transition-colors hover:bg-primary/10 group"
      data-testid="help-next-step-btn"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15">
        <NextIcon className="h-3.5 w-3.5 text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Next step</div>
        <div className="text-[12px] font-medium text-foreground">{nextMeta.label}</div>
        <div className="text-[10.5px] text-muted-foreground leading-snug">{next.reason}</div>
      </div>
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary opacity-50 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

/* ─── Main drawer ─────────────────────────────────────────────────────── */

export default function ContextHelpDrawer({ open: drawerOpen, onClose }) {
  const [panel, setPanel] = useState('home');
  const [query, setQuery] = useState('');
  const [overrides, setOverrides] = useState({});   // DB-saved panel overrides
  const searchRef = useRef(null);

  /* Fetch drawer panel overrides from the backend every time the drawer opens.
   * The endpoint is lightweight (MongoDB read, no LLM). Falls back silently
   * to hardcoded PANEL_TIPS on any network error. */
  useEffect(() => {
    if (!drawerOpen) return;
    const API = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/help/drawer-panels`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && typeof data === 'object') setOverrides(data); })
      .catch(() => {});   // silent — code defaults remain active
  }, [drawerOpen]);

  useEffect(() => {
    if (drawerOpen) {
      setPanel(window.__loomCurrentPanel || 'home');
      setQuery('');
    }
  }, [drawerOpen]);

  const navigate = (key) => {
    if (PANEL_META[key]) {
      setPanel(key);
      setQuery('');
    }
  };

  /* Return DB data if it has been saved, otherwise fall back to PANEL_TIPS */
  const getCfg = (key) => {
    const db = overrides[key];
    if (db && Array.isArray(db.sections) && db.sections.length > 0) return db;
    return PANEL_TIPS[key] || PANEL_TIPS.home;
  };

  const parsed = query.trim() ? parseBooleanQuery(query) : null;
  const searchResults = parsed ? runSearch(parsed) : null;
  const highlightTerms = parsed ? getHighlightTerms(parsed) : [];

  const cfg = getCfg(panel);
  const meta = PANEL_META[panel] || PANEL_META.home;
  const PanelIcon = meta.icon;

  return (
    <Sheet open={drawerOpen} onOpenChange={(v) => { if (!v) onClose(); }} modal={false}>
      <SheetContent
        side="right"
        className="w-[22rem] p-0 flex flex-col gap-0 overflow-hidden"
        hideOverlay
        data-testid="context-help-drawer"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b border-border bg-muted/30 shrink-0">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-rose-700" />
            <SheetTitle className="text-sm font-semibold">Context Help</SheetTitle>
          </div>
          {!query.trim() && (
            <div className="flex items-center gap-1.5 mt-1">
              <PanelIcon className={cn('h-3.5 w-3.5', meta.colour)} />
              <span className={cn('text-[11px] font-medium', meta.colour)}>{meta.label}</span>
            </div>
          )}
        </SheetHeader>

        {/* Search bar */}
        <div className="px-3 pt-2.5 pb-2 shrink-0 border-b border-border/60">
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search help… (AND, OR, NOT, -term)"
              className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-7 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-rose-700/40"
              data-testid="help-search-input"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 text-muted-foreground hover:text-foreground"
                data-testid="help-search-clear"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          {query.trim() && (
            <p className="mt-1 text-[9.5px] text-muted-foreground">
              {searchResults?.length
                ? `${searchResults.length} section${searchResults.length !== 1 ? 's' : ''} found`
                : 'No matches — try OR to broaden, or -term to exclude'}
            </p>
          )}
        </div>

        {/* Panel switcher chips — hidden while searching */}
        {!query.trim() && (
          <div className="px-3 pt-2.5 pb-1 shrink-0">
            <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wide">Jump to panel</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(PANEL_META).map(([key, { icon: Icon, label, colour }]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPanel(key)}
                  className={cn(
                    'flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors',
                    panel === key
                      ? 'border-rose-700/60 bg-rose-700/10 text-rose-700'
                      : 'border-border bg-transparent text-muted-foreground hover:border-rose-700/30 hover:text-foreground',
                  )}
                  data-testid={`help-context-chip-${key}`}
                >
                  <Icon className="h-2.5 w-2.5" />
                  {label.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content area */}
        <div
          className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
          data-testid="context-help-tips"
        >
          {query.trim() ? (
            /* ── Search results ── */
            searchResults && searchResults.length > 0 ? (
              searchResults.map((entry, i) => (
                <TipSection
                  key={i}
                  section={entry}
                  onNavigate={navigate}
                  panelBadge={{ label: entry.panelLabel, colour: entry.panelColour }}
                  highlightTerms={highlightTerms}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                <Search className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-xs text-muted-foreground">
                  No matches for <strong>"{query}"</strong>
                </p>
                <p className="text-[10.5px] text-muted-foreground/70">
                  Try <code className="font-mono bg-muted px-1 rounded">word OR count</code> to broaden,
                  or <code className="font-mono bg-muted px-1 rounded">-term</code> to exclude.
                </p>
              </div>
            )
          ) : (
            /* ── Normal panel view ── */
            <>
              {(cfg.sections || []).map((section, i) => (
                <TipSection key={i} section={section} onNavigate={navigate} />
              ))}
              <NextStepBanner panel={panel} onNavigate={navigate} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-border px-4 py-2.5 flex items-center justify-between bg-muted/20">
          <span className="text-[10px] text-muted-foreground">Press F1 for the full manual</span>
          <a
            href="/training"
            className="flex items-center gap-1 text-[10px] text-rose-700 hover:underline"
            data-testid="help-drawer-training-link"
          >
            Training library <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
}
